/**
 * Hook for handling data export in DataCloud
 * Manages CSV generation, info file creation, and download functionality
 */
import { useCallback, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataPoint,
  InterpolatedPoint,
  DataCloudUIAction,
  CloudErrorPayload
} from '../types/dataCloud.types';
import { formatTimestamp } from '../utils/chartHelpers';
import { cloudErrorPayloadFromError } from '../utils/cloudErrorPayload';

interface UseDataCloudExportProps {
  file1: File | null;
  file2: File | null;
  chartData: DataPoint[];
  interpolatedData: InterpolatedPoint[];
  yAxisLabel: string;
  xAxisLabel: string;
  regressionType: string;
  toleranceType: string;
  toleranceCnt: string;
  toleranceDep: string;
  toleranceDepExtra: string;
  maxTimeSpan: string;
  saveDataName: string;
  dispatch: Dispatch<DataCloudUIAction>;
}

interface UseDataCloudExportReturn {
  saveData: () => Promise<void>;
  downloadInfoFile: (baseFileName: string) => void;
  generateInfoFileContent: () => string;
}

export const useDataCloudExport = ({
  file1,
  file2,
  chartData,
  interpolatedData,
  yAxisLabel,
  xAxisLabel,
  regressionType,
  toleranceType,
  toleranceCnt,
  toleranceDep,
  toleranceDepExtra,
  maxTimeSpan,
  saveDataName,
  dispatch
}: UseDataCloudExportProps): UseDataCloudExportReturn => {
  const { t } = useTranslation('dataCloud');

  /**
   * Generate info file content with processing parameters
   */
  const generateInfoFileContent = useCallback((): string => {
    const timestamp = formatTimestamp();

    let content = `${t('infoFile.header')}\n`;
    content += `${'='.repeat(60)}\n\n`;

    // Generation timestamp
    content += `${t('infoFile.generatedAt')}: ${timestamp}\n\n`;

    // Uploaded files
    if (file1 || file2) {
      content += `${t('infoFile.uploadedFiles')}:\n`;
      content += `${'-'.repeat(40)}\n`;
      if (file1) {
        content += `${t('infoFile.predictors')}: ${file1.name}\n`;
      }
      if (file2) {
        content += `${t('infoFile.targetVariable')}: ${file2.name}\n`;
      }
      content += `\n`;
    }

    // Processing parameters
    content += `${t('infoFile.parameters')}:\n`;
    content += `${'-'.repeat(40)}\n`;

    // Regression type
    if (regressionType) {
      const regressionLabel =
        regressionType === 'lin'
          ? t('infoFile.linear')
          : t('infoFile.polynomial');
      content += `${t('infoFile.regressionType')}: ${regressionLabel}\n`;
    }

    // Tolerance type - use uppercase abbreviations (CNT/DEP) as technical identifiers
    if (toleranceType) {
      const toleranceLabel = toleranceType.toUpperCase();
      content += `${t('infoFile.toleranceType')}: ${toleranceLabel}\n`;
    }

    // Tolerance values
    if (toleranceType === 'cnt' && toleranceCnt) {
      content += `${t('infoFile.toleranceCnt')}: ${toleranceCnt}\n`;
    }
    if (toleranceType === 'dep') {
      if (toleranceDep) {
        content += `${t('infoFile.toleranceDep')}: ${toleranceDep}\n`;
      }
      if (toleranceDepExtra) {
        content += `${t('infoFile.toleranceDepExtra')}: ${toleranceDepExtra}\n`;
      }
    }

    // Max time span
    if (maxTimeSpan) {
      content += `${t('infoFile.maxTimeSpan')}: ${maxTimeSpan} min\n`;
    }

    content += `\n${'='.repeat(60)}\n`;

    return content;
  }, [
    file1,
    file2,
    xAxisLabel,
    yAxisLabel,
    regressionType,
    toleranceType,
    toleranceCnt,
    toleranceDep,
    toleranceDepExtra,
    maxTimeSpan,
    t
  ]);

  /**
   * Download info file as text
   */
  const downloadInfoFile = useCallback(
    (baseFileName: string) => {
      try {
        const infoContent = generateInfoFileContent();
        const blob = new Blob([infoContent], { type: 'text/plain;charset=utf-8' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${baseFileName}_info.txt`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Error downloading info file:', error);
        // Don't show error to user as this is a supplementary file
      }
    },
    [generateInfoFileContent]
  );

  /**
   * Save data to CSV file
   */
  const saveData = useCallback(async () => {
    try {
      if (!file1 || !file2) {
        const payload: CloudErrorPayload = {
          code: undefined,
          suggestion: undefined,
          fallback: t('errors.bothFilesRequired')
        };
        dispatch({ type: 'SET_ERROR', payload });
        dispatch({ type: 'SET_SUCCESS', payload: null });
        return;
      }

      // Check if we have data to save
      const hasInterpolatedData = interpolatedData && interpolatedData.length > 0;
      const hasChartData = chartData && chartData.length > 0;

      if (!hasInterpolatedData && !hasChartData) {
        const payload: CloudErrorPayload = {
          code: undefined,
          suggestion: undefined,
          fallback: t('errors.noDataToSave')
        };
        dispatch({ type: 'SET_ERROR', payload });
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });

      let transformedData: { UTC: string; value: number }[];

      // If we have interpolated data, use it directly
      if (hasInterpolatedData) {
        transformedData = interpolatedData;
      } else {
        // Otherwise use chart data with transformation
        const sortedData = [...chartData].sort((a, b) => a.x - b.x);

        // Get min and max x values
        const minX = sortedData[0].x;
        const maxX = sortedData[sortedData.length - 1].x;
        const totalRange = maxX - minX;

        // Create base date
        const baseDate = new Date('2024-12-16T11:55:13+01:00');
        const utcBase = new Date(
          baseDate.getTime() - baseDate.getTimezoneOffset() * 60000
        );

        transformedData = sortedData.map(point => {
          // Calculate relative position in time range (0 to 1)
          const relativePosition = totalRange > 0 ? (point.x - minX) / totalRange : 0;

          // Create a new date by adding proportional minutes to base time
          const timestamp = new Date(
            utcBase.getTime() + relativePosition * 24 * 60 * 60 * 1000
          ); // Spread over 24 hours

          // Use the predicted value if available, or the original y value
          const value = point.predicted !== undefined ? point.predicted : point.y;

          return {
            UTC: timestamp.toISOString().replace('T', ' ').slice(0, 19),
            value: value
          };
        });
      }

      // Create CSV content directly (semicolon separator for German locale)
      const fileName = saveDataName || 'interpolated_data';
      const csvContent = [
        `UTC;${yAxisLabel}`,
        ...transformedData.map(point => `${point.UTC};${point.value}`)
      ].join('\n');

      // Create Blob and trigger download directly
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${fileName}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      // Automatically download info file with same base name
      downloadInfoFile(fileName);

      dispatch({ type: 'SET_SUCCESS', payload: { key: 'success.dataSaved' } });
      dispatch({ type: 'SET_ERROR', payload: null });
      dispatch({ type: 'MARK_STEP_COMPLETED', payload: 5 });
      dispatch({ type: 'TOGGLE_MODAL', payload: false });
    } catch (error) {
      console.error('Error saving data:', error);
      const payload = cloudErrorPayloadFromError(error);
      // Prefer feature-specific FE fallback when the caught error lacks a BE code/message
      if (!payload.code && (!payload.fallback || payload.fallback === '')) {
        payload.fallback = t('errors.saveFailed');
      }
      dispatch({ type: 'SET_ERROR', payload });
      dispatch({ type: 'SET_SUCCESS', payload: null });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [
    file1,
    file2,
    chartData,
    interpolatedData,
    yAxisLabel,
    saveDataName,
    dispatch,
    t,
    downloadInfoFile
  ]);

  return {
    saveData,
    downloadInfoFile,
    generateInfoFileContent
  };
};

export default useDataCloudExport;
